// Foreground + adaptive-throttled location bridge.
//
// Real background tracking requires a custom Capacitor plugin wrapping CLLocationManager —
// see TODO-IOS.md. Until then, this module:
//   - uses @capacitor/geolocation watchPosition on native (foreground)
//   - uses navigator.geolocation.watchPosition on web
//   - applies an ADAPTIVE downsampler in TS so the same throttling rules ship to both:
//       * emit a ping if device has moved >= movingDistanceM since last emit  (default 250 m)
//       * after stoppedAfterMs of no movement, downshift to one ping per stoppedIntervalMs
//         (default 5 min / sig-change-ish density)
//     The native plugin will eventually push these decisions into CoreLocation itself
//     (distanceFilter + startMonitoringSignificantLocationChanges) for true background savings.
import { Geolocation, type Position } from "@capacitor/geolocation";
import { isNative } from "./platform";
import { LocationBG, drainNativeLocationQueue } from "./location-bg";
import { requestLocationAlways } from "./permissions";
import { getCachedTripSync } from "@/lib/offline/trip-cache";

export type Ping = {
  lat: number;
  lng: number;
  accuracy: number | null;
  altitude: number | null;
  speed: number | null;
  heading: number | null;
  capturedAt: string; // ISO
  source: "native_ios_foreground" | "native_ios_background" | "web_foreground";
};

export type AdaptiveOptions = {
  movingDistanceM?: number;  // emit when moved at least this far (default 250)
  stoppedAfterMs?: number;   // declare "stopped" after this much no-movement (default 5 min)
  stoppedIntervalMs?: number; // when stopped, emit at most every N ms (default 5 min)
  /**
   * Preset-change reapply path only. When true, the first incoming fix is
   * absorbed as a baseline (seeds `lastEmitted` + `lastMovedAt`) and is NOT
   * forwarded to `onPing`. Prevents a ghost route point on preset change.
   */
  suppressFirstFix?: boolean;
};

/** Hard accuracy floor for automatic route points (manual GPS has its own gates). */
const MAX_AUTO_ACCURACY_M = 250;

function fromPosition(p: Position, source: Ping["source"]): Ping {
  return {
    lat: p.coords.latitude,
    lng: p.coords.longitude,
    accuracy: p.coords.accuracy ?? null,
    altitude: p.coords.altitude ?? null,
    speed: p.coords.speed ?? null,
    heading: p.coords.heading ?? null,
    capturedAt: new Date(p.timestamp || Date.now()).toISOString(),
    source,
  };
}

// Haversine, returns meters.
function distanceM(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export async function requestLocationPermission(): Promise<"granted" | "denied" | "prompt"> {
  if (!isNative()) return "prompt";
  // Two-step: WhenInUse first, then upgrade to Always via the LocationBG plugin so background
  // tracking keeps recording when the phone is locked. iOS gates the Always prompt behind a
  // prior WhenInUse grant + at least one background session, so first install will show only
  // "Allow While Using App"; the upgrade banner in the tracking screen handles the second ask.
  const state = await requestLocationAlways();
  if (state === "granted" || state === "limited") return "granted";
  if (state === "denied") return "denied";
  return "prompt";
}

/** Reports whether the user has granted "Always" (background) vs just "While Using App". */
export async function getLocationAuthLevel(): Promise<"always" | "whenInUse" | "denied" | "prompt" | "unknown"> {
  if (!isNative()) return "unknown";
  try {
    const res = await LocationBG.getAuthStatus();
    return res.status === "restricted" ? "denied" : res.status;
  } catch {
    // Native plugin not in this build. @capacitor/geolocation can't distinguish
    // always vs whenInUse, so return "unknown" rather than lie with "whenInUse".
    try {
      const res = await Geolocation.checkPermissions();
      if (res.location === "denied") return "denied";
    } catch { /* ignore */ }
    return "unknown";
  }
}

export type WatchHandle = {
  stop: () => Promise<void>;
  /**
   * Inject a foreground-only synthetic position through the same adaptive
   * gates (movement threshold, stopped heartbeat interval) that real
   * locationUpdate callbacks use. Lets callers (e.g. active-watch's
   * foreground soft-heartbeat timer) refresh "Last GPS" while the user is
   * stationary on iOS — where CoreLocation otherwise delivers no callbacks
   * because the distanceFilter isn't crossed. Does NOT bypass gates: if the
   * point doesn't qualify, nothing is emitted.
   */
  pump: (p: Ping) => void;
};

/**
 * One-shot location read, returned as a Ping with the appropriate source tag.
 * Used by the foreground soft-heartbeat in active-watch. Foreground-only by
 * nature — iOS will refuse `getCurrentPosition` when the WebView is suspended.
 */
export async function getCurrentPing(): Promise<Ping> {
  const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 15_000 });
  return fromPosition(pos, isNative() ? "native_ios_foreground" : "web_foreground");
}

/**
 * Lower-accuracy one-shot read. Uses network/cell-tower triangulation when
 * available so it can return quickly indoors / at an airport gate where a
 * cold GPS fix may take 30+ seconds. Returned ping is still tagged with the
 * normal foreground source — accuracy field reflects reality (>= ~100 m).
 */
export async function getCurrentPingLowAccuracy(timeoutMs = 8_000): Promise<Ping> {
  const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: timeoutMs });
  return fromPosition(pos, isNative() ? "native_ios_foreground" : "web_foreground");
}


export async function watchPosition(
  onPing: (p: Ping) => void,
  onError: (msg: string) => void,
  opts: AdaptiveOptions = {},
): Promise<WatchHandle> {
  const movingDistanceM = opts.movingDistanceM ?? 75;
  const stoppedAfterMs = opts.stoppedAfterMs ?? 2 * 60_000;
  const stoppedIntervalMs = opts.stoppedIntervalMs ?? 2 * 60_000;

  let lastEmitted: Ping | null = null;
  let lastMovedAt = Date.now();
  let suppressFirst = !!opts.suppressFirstFix;

  function consider(p: Ping) {
    // Hard accuracy floor — automatic route points with extreme uncertainty
    // are dropped before they reach the outbox / map / Live Activity stats.
    // Manual GPS has its own (stricter) gates in manual-gps.ts.
    if (
      p.accuracy != null &&
      Number.isFinite(p.accuracy) &&
      p.accuracy > MAX_AUTO_ACCURACY_M
    ) {
      console.info(`[active-watch] drop low-acc acc=${Math.round(p.accuracy)}m source=${p.source}`);
      return;
    }
    if (!lastEmitted) {
      if (suppressFirst) {
        // Preset-change reapply — absorb baseline silently, do not emit.
        suppressFirst = false;
        lastEmitted = p;
        lastMovedAt = Date.now();
        console.info(`[active-watch] suppressed first fix after preset change source=${p.source}`);
        return;
      }
      // First ping always emits immediately so the UI gets instant feedback.
      lastEmitted = p;
      lastMovedAt = Date.now();
      onPing(p);
      return;
    }
    const moved = distanceM(lastEmitted, p);
    const sinceMoved = Date.now() - lastMovedAt;
    const stopped = sinceMoved >= stoppedAfterMs;
    const sinceEmit = new Date(p.capturedAt).getTime() - new Date(lastEmitted.capturedAt).getTime();

    if (moved >= movingDistanceM) {
      lastEmitted = p;
      lastMovedAt = Date.now();
      onPing(p);
    } else if (stopped && sinceEmit >= stoppedIntervalMs) {
      // Heartbeat so a parked user still sees pings tick up.
      lastEmitted = p;
      onPing(p);
    }
  }

  if (isNative()) {
    // Prefer the custom LocationBG plugin — it owns CoreLocation directly with
    // allowsBackgroundLocationUpdates=true, so tracking continues while the phone is locked.
    let step = "addListener:locationUpdate";
    try {
      const handle = await LocationBG.addListener("locationUpdate", (p) => {
        // Lifetime counter for the /offline-control reconciliation card.
        // Every native fix the JS side observes is counted, regardless of
        // whether `consider()` decides to emit it downstream. Persisted to
        // localStorage so it survives reloads.
        try {
          if (typeof localStorage !== "undefined") {
            const k = "roava.captureCounter.lifetime";
            const cur = Number(localStorage.getItem(k) ?? "0");
            localStorage.setItem(k, String((Number.isFinite(cur) ? cur : 0) + 1));
          }
        } catch { /* quota — ignore */ }
        consider({
          lat: p.lat,
          lng: p.lng,
          accuracy: p.accuracy,
          altitude: p.altitude,
          speed: p.speed,
          heading: p.heading,
          capturedAt: p.capturedAt,
          source: "native_ios_background",
        });
      });
      // eslint-disable-next-line no-console
      console.info(`[LocationBG] step=${step} ok`);

      step = "addListener:locationError";
      const errHandle = await LocationBG.addListener("locationError", (e) => onError(e.message));
      // eslint-disable-next-line no-console
      console.info(`[LocationBG] step=${step} ok`);

      // Layer 7 — native drain event listener. Reliability still comes from
      // the multi-trigger JS pollers (lifecycle/network/route mounts); this
      // event is a best-effort speed-up only.
      step = "addListener:queueDrainRequested";
      const drainHandle = await LocationBG.addListener("queueDrainRequested", () => {
        const tripId = getCachedTripSync()?.id ?? null;
        void drainNativeLocationQueue(tripId);
      });
      // eslint-disable-next-line no-console
      console.info(`[LocationBG] step=${step} ok`);

      step = "startWatch";
      const startRes = await LocationBG.startWatch({
        distanceFilter: movingDistanceM,
        stoppedAfterMs,
        stoppedIntervalMs,
      });
      // eslint-disable-next-line no-console
      console.info(
        `[LocationBG] step=${step} ok — native background watcher live ` +
        `df=${startRes?.distanceFilter ?? movingDistanceM}m ` +
        `stoppedAfter=${startRes?.stoppedAfterMs ?? stoppedAfterMs}ms ` +
        `stoppedInterval=${startRes?.stoppedIntervalMs ?? stoppedIntervalMs}ms ` +
        `heartbeat=${startRes?.heartbeatCadenceMs ?? "?"}ms`,
      );

      // Immediate drain after startWatch resolves — picks up any points left
      // over from a previous session that ended before JS could drain them.
      void drainNativeLocationQueue(getCachedTripSync()?.id ?? null);

      return {
        stop: async () => {
          try { await LocationBG.stopWatch(); } catch (e: any) {
            console.warn(`[LocationBG] stopWatch failed: ${e?.message ?? e}`);
          }
          try { await handle.remove(); } catch { /* noop */ }
          try { await errHandle.remove(); } catch { /* noop */ }
          try { await drainHandle.remove(); } catch { /* noop */ }
        },
        pump: consider,
      };
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      const unimplemented = /UNIMPLEMENTED|not implemented|is not a function|no such method/i.test(msg);
      // eslint-disable-next-line no-console
      console.warn(
        `[LocationBG] fallback reason: step=${step} unimplemented=${unimplemented} error=${msg}`,
      );
      // Fire a one-shot read immediately so the UI gets a pin within ~1s instead of
      // waiting for the first watchPosition callback (which can take 10–30s on iOS).
      try {
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 15_000 });
        consider(fromPosition(pos, "native_ios_foreground"));
      } catch (initErr) {
        console.warn("[geolocation] initial getCurrentPosition failed", initErr);
      }
      const id = await Geolocation.watchPosition(
        { enableHighAccuracy: true, timeout: 60_000, maximumAge: 30_000 },
        (pos, err) => {
          if (err) { onError(err.message); return; }
          if (pos) consider(fromPosition(pos, "native_ios_foreground"));
        },
      );
      return { stop: async () => { await Geolocation.clearWatch({ id }); }, pump: consider };
    }
  }

  if (!("geolocation" in navigator)) {
    onError("Geolocation unsupported.");
    return { stop: async () => {}, pump: consider };
  }
  // One-shot first read for instant UI feedback on web too.
  navigator.geolocation.getCurrentPosition(
    (pos) => consider(fromPosition(pos as unknown as Position, "web_foreground")),
    () => { /* ignore; watchPosition will surface the real error */ },
    { enableHighAccuracy: true, maximumAge: 30_000, timeout: 15_000 },
  );
  const wid = navigator.geolocation.watchPosition(
    (pos) => consider(fromPosition(pos as unknown as Position, "web_foreground")),
    (err) => onError(err.message),
    { enableHighAccuracy: true, maximumAge: 30_000, timeout: 60_000 },
  );
  return { stop: async () => navigator.geolocation.clearWatch(wid), pump: consider };
}
